import { isDefined } from '#misc/is-defined.guard';
import { isEmpty } from '#misc/is-empty';
import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * Finds the index of the first item in an array that matches a specified property and value.
 * @param items - The array of items to search through.
 * @param property - The property to check against each item's value. If null or undefined, the entire item is checked.
 * @param value - The value to match against the specified property.
 * @param ignoreCase - Whether to ignore case when matching strings (default is true).
 * @returns The index of the first matching item, or -1 if no match is found.
 *
 * @example
 * ```typescript
 * const users = [{ name: 'Alice' }, { name: 'Bob' }, { name: 'alice' }];
 * findIndex(users, 'name', 'alice'); // Returns 0 (case-insensitive match)
 * findIndex(users, 'name', 'Charlie'); // Returns -1 (no match)
 * ```
 */
export const findIndex = <T, TK extends keyof T>(
  items: readonly T[],
  property: TK | null | undefined,
  value: unknown,
  ignoreCase = true
): number => {
  if (isEmpty(items)) {
    return -1;
  }

  const values = items.map((item) => {
    return isEmpty(item) ? null : isDefined(property) ? item[property] : item;
  });
  const { length } = values;

  for (let i = 0; i < length; i++) {
    const item = values[i];

    if (isNotDefined(item)) {
      continue;
    }

    const regexp = new RegExp(`^${String(item)}$`, ignoreCase ? 'i' : '');

    if (regexp.test(String(value))) {
      return i;
    }
  }

  return -1;
};
